import {
  getMetricLifecycleValidationError,
  getMetricPeriodAllocationType,
  isMetricAllocationTypeSupported,
  normalizeMetricBatchLifecycleValues,
} from '../utils/metric-lifecycle';

export const metricTypeOptions = [
  { label: '原子指标', value: 'atomic' },
  { label: '复合指标', value: 'composite' },
];

export const statisticalModelTypeOptions = [
  { label: '就近复用', value: 'nearest_reuse' },
  { label: '线性外推', value: 'linear_extrapolation' },
];

export const statisticalModelTypeValueEnum = Object.fromEntries(
  statisticalModelTypeOptions.map(({ label, value }) => [value, { text: label }]),
);

export const periodTypeOptions = [
  { label: '日', value: 'day' },
  { label: '月', value: 'month' },
];

export const periodTypeValueEnum = Object.fromEntries(
  periodTypeOptions.map(({ label, value }) => [value, { text: label }]),
);

export const getMetricPeriodTypeOptions = ({ sourceDomain, valueType } = {}) =>
  valueType === 'ratio'
    ? periodTypeOptions
    : periodTypeOptions.filter(({ value }) =>
        isMetricAllocationTypeSupported(sourceDomain, getMetricPeriodAllocationType(value)),
      );

export const getEffectiveMetricPeriodType = ({ periodType } = {}) => periodType || null;

export const allocationTypeOptions = [
  { label: '事件日生效', value: 'none' },
  { label: '指定周期生效', value: 'specified_period' },
  { label: '支付日至月末生效', value: 'payment_to_month_end' },
  { label: '上月生效', value: 'previous_month' },
  { label: '本月生效', value: 'current_month' },
  { label: '下月生效', value: 'next_month' },
  { label: '租期次月生效', value: 'rental_next_month' },
];

export const allocationTypeValueEnum = Object.fromEntries(
  allocationTypeOptions.map(({ label, value }) => [value, { text: label }]),
);

export const getMetricAllocationTypeOptions = (sourceDomain, metric = {}) =>
  allocationTypeOptions.filter(
    ({ value }) =>
      isMetricAllocationTypeSupported(sourceDomain, value) &&
      (!metric.valueType ||
        !getMetricLifecycleValidationError({
          ...metric,
          allocationType: value,
          metricType: 'atomic',
          sourceDomain,
        })),
  );

export const attributionTypeOptions = [
  { label: '当前门店', value: 'direct' },
  { label: '总部管理费用分摊', value: 'headquarters_management_expense' },
];

export const attributionTypeValueEnum = Object.fromEntries(
  attributionTypeOptions.map(({ label, value }) => [value, { text: label }]),
);

export const sourceDomainValueEnum = {
  expense: { text: '经营费用' },
  income: { text: '经营收入' },
  plan: { text: '经营计划' },
  salary: { text: '门店工资' },
};

export const isMetricBatchEditable = ({
  mappingComboCount,
  metricType,
  sourceDomain,
  valueType,
} = {}) =>
  metricType === 'atomic' &&
  valueType === 'amount' &&
  Number(mappingComboCount || 0) > 0 &&
  ['expense', 'salary'].includes(sourceDomain);

export const getMetricSourceDomainPresentation = ({
  mappingComboCount,
  metricType,
  sourceDomain,
} = {}) => {
  if (metricType === 'composite') {
    return '-';
  }
  if (Number(mappingComboCount || 0) === 0) {
    return '未映射';
  }
  return sourceDomainValueEnum[sourceDomain]?.text || '配置异常';
};

export const getMetricBatchUpdateValues = ({ fields = [], ...values } = {}) => {
  const result = normalizeMetricBatchLifecycleValues({ fields, ...values });
  if (fields.includes('attributionType')) {
    result.attributionType = values.attributionType;
  }
  return result;
};

export const DEFAULT_METRIC_ALLOCATION_TYPE = 'none';

export const getMetricDefaultAllocationType = (sourceDomain) => {
  if (sourceDomain === 'salary') {
    return 'next_month';
  }
  return sourceDomain === 'plan' ? 'specified_period' : DEFAULT_METRIC_ALLOCATION_TYPE;
};

export const getMetricDefaultPeriodType = (sourceDomain, valueType) => {
  if (valueType !== 'ratio') {
    return null;
  }
  return ['salary', 'plan'].includes(sourceDomain) ? 'month' : 'day';
};

export const isMappingMetricValueTypeCompatible = (
  sourceDomain,
  sourceValueType,
  metricValueType,
) =>
  sourceDomain === 'expense' ? metricValueType === 'amount' : metricValueType === sourceValueType;

export const isMappingMetricCompatible = (sourceDomain, sourceValueType, metric = {}) => {
  if (
    metric.metricType !== 'atomic' ||
    !isMappingMetricValueTypeCompatible(sourceDomain, sourceValueType, metric.valueType)
  ) {
    return false;
  }
  return !getMetricLifecycleValidationError({ ...metric, sourceDomain });
};

export const getMetricAllocationPresentation = ({
  allocationType,
  mappingComboCount,
  metricType,
  sourceDomain,
}) => {
  if (metricType === 'composite') {
    return {
      allocationType: null,
      editable: false,
      sourceDomain: null,
      status: 'not_applicable',
    };
  }
  if (Number(mappingComboCount || 0) === 0) {
    return {
      allocationType: null,
      editable: false,
      sourceDomain: null,
      status: 'unmapped',
    };
  }
  if (!sourceDomain) {
    return {
      allocationType: null,
      editable: false,
      sourceDomain: null,
      status: 'cross_domain',
    };
  }
  return {
    allocationType,
    editable: sourceDomain === 'expense',
    sourceDomain,
    status: 'effective',
  };
};

export const getEffectiveMetricAllocationType = ({ allocationType, mapped }) =>
  mapped ? (allocationType ?? null) : null;
